import { Component, input, output, computed } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Returns list of organization statuses considered "complete"
 */
function charityProfileStatusComplete(): string[] {
  return ['LIVE', 'PENDING', 'IDLE', 'DECLINED', 'ARCHIVED'];
}

/**
 * Returns list of organization statuses considered "incomplete"
 */
function charityProfileStatusIncomplete(): string[] {
  return ['DRAFT'];
}

/**
 * A reusable card component that displays charity information with a status badge, message, and call-to-action button.
 * Automatically determines the appropriate message and button text based on the organization status.
 * Based on Figma design.
 * Figma URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1329-64643
 *
 * @example
 * ```html
 * <app-charity-profile-card
 *   charityName="Arab Baptist Theological Seminary"
 *   charityLogo="assets/charity-logo.png"
 *   status="LIVE"
 *   (buttonClicked)="goToProfile()">
 * </app-charity-profile-card>
 * ```
 */
@Component({
  selector: 'app-charity-profile-card',
  templateUrl: './charity-profile-card.component.html',
  styleUrls: ['./charity-profile-card.component.scss'],
  standalone: false,
})
export class CharityProfileCardComponent {
  /**
   * The name of the charity
   */
  charityName = input<string>('');

  /**
   * URL/path to the charity logo image
   */
  charityLogo = input<string>('');

  /**
   * Status of the charity organization (e.g., 'LIVE', 'DRAFT', 'INACTIVE', 'ARCHIVED')
   */
  status = input<string>('DRAFT');

  /**
   * Translation key for the message when profile is complete
   */
  completeMessageKey = input<string>('home.charity_profile.description_complete');

  /**
   * Translation key for the message when profile setup is needed
   */
  setupMessageKey = input<string>('home.setup_profile.finish_charity_profile');

  /**
   * Translation key for the button when profile is complete
   */
  completeActionKey = input<string>('home.charity_project.go_charity_profile');

  /**
   * Translation key for the button when profile setup is needed
   */
  setupActionKey = input<string>('home.complete_charity_profile.title');

  /**
   * Translation key for the title when profile is unavailable
   */
  unavailableTitleKey = input<string>('home.setup_profile.setup_charity_profile');

  /**
   * Translation key for the subtitle when profile is unavailable
   */
  unavailableSubtitleKey = input<string>('home.setup_profile.tell_donors');

  /**
   * Translation key for the main description when profile is unavailable
   */
  unavailableMessageKey = input<string>('home.setup_profile.build_trust');

  /**
   * Translation key for the agreement text when profile is unavailable
   */
  unavailableSecondaryMessageKey = input<string>('home.setup_profile.build_trust_2');

  /**
   * Translation key for the button action when profile is unavailable
   */
  unavailableActionKey = input<string>('home.setup_profile.setup_charity_profile');

  /**
   * SVG string for the user icon displayed in unavailable state.
   * Defaults to a placeholder. Replace with actual SVG string.
   */
  userIcon = input<string>(`<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 11C12.4853 11 14.5 8.98528 14.5 6.5C14.5 4.01472 12.4853 2 10 2C7.51472 2 5.5 4.01472 5.5 6.5C5.5 8.98528 7.51472 11 10 11Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M2 20C2 16.6863 5.58172 14 10 14C14.4183 14 18 16.6863 18 20" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`);

  /**
   * Emits when the button is clicked
   */
  buttonClicked = output<void>();

  /**
   * Determines if the component is in unavailable state (no profile exists)
   */
  isUnavailableState = computed(() => {
    return !this.status() || this.status() === '';
  });

  /**
   * Determines if status badge should be shown
   */
  showStatus = computed(() => {
    return !this.isUnavailableState();
  });

  /**
   * Returns the appropriate title key based on state
   */
  titleKey = computed(() => {
    return this.isUnavailableState() ? this.unavailableTitleKey() : '';
  });

  /**
   * Returns the appropriate subtitle key based on state
   */
  subtitleKey = computed(() => {
    return this.isUnavailableState() ? this.unavailableSubtitleKey() : '';
  });

  /**
   * Returns the secondary message key (only for unavailable state)
   */
  secondaryMessageKey = computed(() => {
    return this.unavailableSecondaryMessageKey();
  });

  /**
   * Computed translation key for the message text based on status
   */
  messageKey = computed(() => {
    if (this.isUnavailableState()) {
      return this.unavailableMessageKey();
    }
    const isComplete = charityProfileStatusComplete().includes(this.status());
    return isComplete
      ? this.completeMessageKey()
      : this.setupMessageKey();
  });

  /**
   * Computed translation key for the button text based on status
   */
  buttonTextKey = computed(() => {
    if (this.isUnavailableState()) {
      return this.unavailableActionKey();
    }
    const isComplete = charityProfileStatusComplete().includes(this.status());
    return isComplete
      ? this.completeActionKey()
      : this.setupActionKey();
  });

  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Returns sanitized HTML for the user icon SVG.
   * This bypasses Angular's default sanitization to allow SVG rendering.
   */
  getSafeUserIcon(): SafeHtml {
    const iconValue = this.userIcon();
    return this.sanitizer.bypassSecurityTrustHtml(iconValue || '');
  }

  /**
   * Handles button click events
   */
  onButtonClick(): void {
    this.buttonClicked.emit();
  }
}
